import { Query, QueryClient, QueryKey } from "@tanstack/react-query";

interface DataMessage {
    kind: "data";
    queryKeyParts: QueryKey;
    queryHash: string;
    dataUpdatedAt: number;
    data: Query["state"]["data"];
}

interface RemovalMessage {
    kind: "removed";
    queryHash: string;
}

type SyncMessage = DataMessage | RemovalMessage;

interface SyncQueryCacheAcrossTabsParams {
    queryClient: QueryClient;
    channelName: string;
}

const isSyncMessage = (value: object): value is SyncMessage => {
    if (
        !("kind" in value) ||
        !("queryHash" in value) ||
        typeof value.queryHash !== "string"
    ) {
        return false;
    }
    if (value.kind === "removed") {
        return true;
    }
    return (
        value.kind === "data" &&
        "queryKeyParts" in value &&
        Array.isArray(value.queryKeyParts) &&
        "dataUpdatedAt" in value &&
        typeof value.dataUpdatedAt === "number" &&
        "data" in value
    );
};

/**
 * Shares freshly loaded query data between tabs of the same build.
 *
 * Query creation is never forwarded: a tab that merely starts observing a
 * query would otherwise reset that query to pending (no data) in every other
 * tab, which unmounts whatever is rendered from it (for example the signed-in
 * app when the user status query is wiped). For the same reason a removal only
 * drops a query in tabs where nothing is observing it.
 *
 * Does nothing where BroadcastChannel is unavailable (Safari before 15.4).
 */
export function syncQueryCacheAcrossTabs({
    queryClient,
    channelName,
}: SyncQueryCacheAcrossTabsParams): () => void {
    if (typeof BroadcastChannel === "undefined") {
        return () => undefined;
    }

    const channel = new BroadcastChannel(channelName);
    const queryCache = queryClient.getQueryCache();
    let isApplyingRemoteUpdate = false;

    const post = (message: SyncMessage) => {
        try {
            channel.postMessage(message);
        } catch {
            // Data that is not structured-cloneable (DataCloneError) stays local.
        }
    };

    const unsubscribe = queryCache.subscribe((event) => {
        if (isApplyingRemoteUpdate) {
            return;
        }

        if (event.type === "removed") {
            if (event.query.getObserversCount() > 0) {
                post({ kind: "removed", queryHash: event.query.queryHash });
            }
            return;
        }

        if (event.type !== "updated" || event.action.type !== "success") {
            return;
        }

        const { queryKey, queryHash, state } = event.query;
        if (state.data === undefined) {
            return;
        }

        post({
            kind: "data",
            queryKeyParts: queryKey,
            queryHash,
            dataUpdatedAt: state.dataUpdatedAt,
            data: state.data,
        });
    });

    const applyMessage = (message: SyncMessage) => {
        const existing = queryCache.get(message.queryHash);

        if (message.kind === "removed") {
            if (existing && existing.getObserversCount() === 0) {
                queryCache.remove(existing);
            }
            return;
        }

        if (existing && existing.state.dataUpdatedAt > message.dataUpdatedAt) {
            return;
        }

        queryClient.setQueryData(message.queryKeyParts, message.data, {
            updatedAt: message.dataUpdatedAt,
        });
    };

    channel.onmessage = (event: MessageEvent) => {
        const payload: object | null =
            typeof event.data === "object" ? event.data : null;
        if (payload === null || !isSyncMessage(payload)) {
            return;
        }

        isApplyingRemoteUpdate = true;
        try {
            applyMessage(payload);
        } finally {
            isApplyingRemoteUpdate = false;
        }
    };

    return () => {
        unsubscribe();
        channel.close();
    };
}
